import React, { useState, useRef, useEffect } from 'react';
import { 
  TrendingUp, Users, ShoppingCart, DollarSign, Calendar, 
  Package, Receipt, MessageSquare, ArrowUp, ArrowDown,
  BarChart3, PieChart, LineChart
} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import ChartComponent from './ChartComponent';

const StatisticsPage = ({ lang }) => {
  const { t } = useTranslation(lang);

  // Références pour la navigation
  const managementRef = useRef(null);
  const financeRef = useRef(null);
  const supportRef = useRef(null);

  const [timeRange, setTimeRange] = useState('month');
  const [hoveredNav, setHoveredNav] = useState(null);

  // Données pour les graphiques Management
  const managementCharts = {
    users: {
      type: 'doughnut',
      data: {
        labels: [t('active'), t('inactive'), t('pending')],
        datasets: [{
          data: [850, 250, 150],
          backgroundColor: ['#10b981', '#ef4444', '#f59e0b'],
          borderWidth: 3,
          borderColor: '#ffffff'
        }]
      },
      options: {
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true
            }
          }
        }
      },
      total: 1250,
      growth: 12.5
    },
    suppliers: {
      type: 'line',
      data: {
        labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [{
          label: t('suppliers'),
          data: [12, 15, 18, 22, 25, 28, 32, 35, 38, 40, 42, 45],
          borderColor: '#7c3aed',
          backgroundColor: 'rgba(124, 58, 237, 0.1)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#7c3aed',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2
        }]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(0, 0, 0, 0.1)'
            }
          }
        }
      },
      total: 45,
      growth: 8.3
    },
    products: {
      type: 'bar',
      data: {
        labels: [t('hotels'), t('activities'), t('flights'), t('transfers')],
        datasets: [{
          label: t('products'),
          data: [120, 85, 65, 50],
          backgroundColor: [
            'rgba(124, 58, 237, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(245, 158, 11, 0.8)'
          ],
          borderColor: [
            '#7c3aed',
            '#10b981',
            '#3b82f6',
            '#f59e0b'
          ],
          borderWidth: 2,
          borderRadius: 8
        }]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true
          }
        }
      },
      total: 320,
      growth: -2.1
    }
  };

  // Données pour les graphiques Finance
  const financeCharts = {
    revenue: {
      type: 'line',
      data: {
        labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [{
          label: t('revenue') + ' (€)',
          data: [85000, 92000, 105000, 98000, 112000, 125000, 118000, 132000, 128000, 135000, 140000, 145000],
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          fill: true
        }]
      },
      total: 145000,
      growth: 15.8
    },
    sales: {
      type: 'pie',
      data: {
        labels: [t('hotels'), t('activities'), t('flights'), t('transfers')],
        datasets: [{
          data: [980, 650, 520, 300],
          backgroundColor: [
            'rgba(124, 58, 237, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(245, 158, 11, 0.8)'
          ],
          borderWidth: 3,
          borderColor: '#ffffff'
        }]
      },
      options: {
        plugins: {
          legend: {
            position: 'right',
            labels: {
              padding: 20
            }
          }
        }
      },
      total: 2450,
      growth: 22.4
    },
    invoices: {
      type: 'doughnut',
      data: {
        labels: [t('paid'), t('pending'), t('overdue')],
        datasets: [{
          data: [1450, 250, 100],
          backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
          borderWidth: 3,
          borderColor: '#ffffff'
        }]
      },
      total: 1800,
      growth: 5.2
    }
  };

  // Données pour les graphiques Support
  const supportCharts = {
    tickets: {
      type: 'bar',
      data: {
        labels: [t('open'), t('inProgress'), t('resolved')],
        datasets: [{
          label: t('tickets'),
          data: [45, 68, 43],
          backgroundColor: [
            'rgba(239, 68, 68, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(16, 185, 129, 0.8)'
          ],
          borderColor: [
            '#ef4444',
            '#f59e0b',
            '#10b981'
          ],
          borderWidth: 2,
          borderRadius: 8
        }]
      },
      total: 156,
      growth: -8.7
    },
    responseTime: {
      type: 'line',
      data: {
        labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
        datasets: [{
          label: t('responseTime') + ' (h)',
          data: [2.8, 2.4, 2.2, 2.5, 2.3, 2.6, 2.9],
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          pointStyle: 'circle',
          pointRadius: 6
        }]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            max: 4
          }
        }
      },
      average: '2.4h',
      growth: -12.3
    },
    satisfaction: {
      type: 'radar',
      data: {
        labels: [t('responseTime'), t('quality'), t('communication'), t('resolution'), t('availability')],
        datasets: [{
          label: t('satisfaction'),
          data: [53, 92, 78, 88, 45],
          backgroundColor: 'rgba(124, 58, 237, 0.2)',
          borderColor: '#7c3aed',
          pointBackgroundColor: '#7c3aed',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }]
      },
      options: {
        scales: {
          r: {
            beginAtZero: true,
            max: 100,
            ticks: {
              stepSize: 20
            }
          }
        }
      },
      average: '86%',
      growth: 4.2
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh',
      position: 'relative'
    },
  topbar: {
    background: 'white',
    borderRadius: 16,             
    padding: '5px 16px',          
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    marginBottom: 8,
    display: 'flex',
    flexWrap: 'wrap',              
    alignItems: 'center',
    gap: 16,                        
    rowGap: 8,
    position: 'sticky',
    top: 75,
    zIndex: 999
  },
  topbarTitle: {
    fontSize: 16,                  
    fontWeight: 600,
    color: '#1f2937',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  navList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexWrap: 'wrap',              
    gap: 8
  },
  navItem: {
    padding: '6px 10px',
    borderRadius: 10,              
    cursor: 'pointer',
    transition: 'all .2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#6b7280',
    fontWeight: 500,
    fontSize: 13
  },
  navItemHover: {
    background: '#f3f4f6',
    color: '#7c3aed'
  },

  '@media (max-width: 600px)': {
    navItem: {
      padding: '6px',               
      minWidth: 34,
      justifyContent: 'center'
    },
    navItemText: { display: 'none' } 
  },

    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '32px',
      marginBottom: '32px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px'
    },
    sectionTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    timeFilter: {
      padding: '8px 16px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      background: 'white',
      cursor: 'pointer'
    },
    chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: '24px'
    },
    chartCard: {
      background: '#f8fafc',
      borderRadius: '12px',
      padding: '24px',
      border: '1px solid #e5e7eb'
    },
    chartHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    chartTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    metricValue: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '8px'
    },
    metricGrowth: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '14px',
      fontWeight: '500'
    },
    growthPositive: {
      color: '#10b981'
    },
    growthNegative: {
      color: '#ef4444'
    },
    chartContainer: {
      height: '300px',
      background: 'white',
      borderRadius: '8px',
      marginTop: '16px',
      padding: '16px',
      position: 'relative'
    }
  };

  return (
    <div style={styles.container}>
      {/* Topbar de navigation */}
      <nav style={styles.topbar}>
        <h2 style={styles.topbarTitle}>
          <TrendingUp size={20} />
          {t('navigation')}
        </h2>
        <ul style={styles.navList}>
          <li 
            style={{
              ...styles.navItem,
              ...(hoveredNav === 'management' && styles.navItemHover)
            }}
            onClick={() => scrollToSection(managementRef)}
            onMouseEnter={() => setHoveredNav('management')}
            onMouseLeave={() => setHoveredNav(null)}
          >
            <Users size={16} />
            {t('management')}
          </li>
          <li 
            style={{
              ...styles.navItem,
              ...(hoveredNav === 'finance' && styles.navItemHover)
            }}
            onClick={() => scrollToSection(financeRef)}
            onMouseEnter={() => setHoveredNav('finance')}
            onMouseLeave={() => setHoveredNav(null)}
          >
            <DollarSign size={16} />
            {t('finance')}
          </li>
          <li 
            style={{
              ...styles.navItem,
              ...(hoveredNav === 'support' && styles.navItemHover)
            }}
            onClick={() => scrollToSection(supportRef)}
            onMouseEnter={() => setHoveredNav('support')}
            onMouseLeave={() => setHoveredNav(null)}
          >
            <MessageSquare size={16} />
            {t('support')}
          </li>
        </ul>
      </nav>

      {/* Contenu principal */}
      <div>
        {/* Section Management */}
        <section ref={managementRef} style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <Users size={28} />
              {t('management')}
            </h2>
            <select 
              style={styles.timeFilter}
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="week">{t('lastWeek')}</option>
              <option value="month">{t('lastMonth')}</option>
              <option value="quarter">{t('lastQuarter')}</option>
              <option value="year">{t('lastYear')}</option>
            </select>
          </div>

          <div style={styles.chartsGrid}>
            {/* Utilisateurs - Doughnut */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <Users size={20} />
                  {t('users')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...(managementCharts.users.growth > 0 ? styles.growthPositive : styles.growthNegative)
                }}>
                  {managementCharts.users.growth > 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {Math.abs(managementCharts.users.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{formatNumber(managementCharts.users.total)}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={managementCharts.users.type}
                  data={managementCharts.users.data}
                  options={managementCharts.users.options}
                />
              </div>
            </div>

            {/* Fournisseurs - Line */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <Package size={20} />
                  {t('suppliers')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...(managementCharts.suppliers.growth > 0 ? styles.growthPositive : styles.growthNegative)
                }}>
                  {managementCharts.suppliers.growth > 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {Math.abs(managementCharts.suppliers.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{formatNumber(managementCharts.suppliers.total)}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={managementCharts.suppliers.type}
                  data={managementCharts.suppliers.data}
                  options={managementCharts.suppliers.options}
                />
              </div>
            </div>

            {/* Produits - Bar */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <ShoppingCart size={20} />
                  {t('products')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...(managementCharts.products.growth > 0 ? styles.growthPositive : styles.growthNegative)
                }}>
                  {managementCharts.products.growth > 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {Math.abs(managementCharts.products.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{formatNumber(managementCharts.products.total)}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={managementCharts.products.type}
                  data={managementCharts.products.data}
                  options={managementCharts.products.options}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section Finance */}
        <section ref={financeRef} style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <DollarSign size={28} />
              {t('finance')}
            </h2>
          </div>

          <div style={styles.chartsGrid}>
            {/* Revenus - Line */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <TrendingUp size={20} />
                  {t('revenue')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...styles.growthPositive
                }}>
                  <ArrowUp size={16} />
                  {Math.abs(financeCharts.revenue.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{formatCurrency(financeCharts.revenue.total)}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={financeCharts.revenue.type}
                  data={financeCharts.revenue.data}
                />
              </div>
            </div>

            {/* Ventes - Pie */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <ShoppingCart size={20} />
                  {t('sales')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...styles.growthPositive
                }}>
                  <ArrowUp size={16} />
                  {Math.abs(financeCharts.sales.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{formatNumber(financeCharts.sales.total)}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={financeCharts.sales.type}
                  data={financeCharts.sales.data}
                  options={financeCharts.sales.options}
                />
              </div>
            </div>

            {/* Factures - Doughnut */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <Receipt size={20} />
                  {t('invoices')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...styles.growthPositive
                }}>
                  <ArrowUp size={16} />
                  {Math.abs(financeCharts.invoices.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{formatNumber(financeCharts.invoices.total)}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={financeCharts.invoices.type}
                  data={financeCharts.invoices.data}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section Support */}
        <section ref={supportRef} style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <MessageSquare size={28} />
              {t('support')}
            </h2>
          </div>

          <div style={styles.chartsGrid}>
            {/* Tickets - Bar */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <MessageSquare size={20} />
                  {t('tickets')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...styles.growthPositive
                }}>
                  <ArrowDown size={16} />
                  {Math.abs(supportCharts.tickets.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{formatNumber(supportCharts.tickets.total)}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={supportCharts.tickets.type}
                  data={supportCharts.tickets.data}
                />
              </div>
            </div>

            {/* Temps de réponse - Line */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <Calendar size={20} />
                  {t('responseTime')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...styles.growthPositive
                }}>
                  <ArrowDown size={16} />
                  {Math.abs(supportCharts.responseTime.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{supportCharts.responseTime.average}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={supportCharts.responseTime.type}
                  data={supportCharts.responseTime.data}
                  options={supportCharts.responseTime.options}
                />
              </div>
            </div>

            {/* Satisfaction - Radar */}
            <div style={styles.chartCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>
                  <BarChart3 size={20} />
                  {t('satisfaction')}
                </h3>
                <div style={{
                  ...styles.metricGrowth,
                  ...styles.growthPositive
                }}>
                  <ArrowUp size={16} />
                  {Math.abs(supportCharts.satisfaction.growth)}%
                </div>
              </div>
              <div style={styles.metricValue}>{supportCharts.satisfaction.average}</div>
              <div style={styles.chartContainer}>
                <ChartComponent 
                  type={supportCharts.satisfaction.type}
                  data={supportCharts.satisfaction.data}
                  options={supportCharts.satisfaction.options}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StatisticsPage;